import { useEffect, useRef, useState, type ChangeEvent } from "react";
import InlineSVG from "react-inlinesvg";
import { ChatSupportRequestFormChrome } from "@sudowrite/shared-chat";
import Auth from "src/Auth";
import { Textarea } from "src/sudokit/components/Textarea";
import { usePersistentChat } from "./PersistentChatContext";

export type ChatSupportRequestFormProps = {
  chatId: string;
  messageId: string;
  toolCallId: string;
  projectId: string;
  invocationId?: string | null;
  /** Agent-written summary; pre-fills the single user-editable field. */
  messageSummary: string;
  category: "billing" | "human_support";
  submissionStatus?: "success" | "error" | null;
};

type SubmitState = "idle" | "submitting" | "success" | "error";

export function ChatSupportRequestForm({
  chatId,
  messageId,
  toolCallId,
  projectId,
  invocationId,
  messageSummary,
  category,
  submissionStatus = null,
}: ChatSupportRequestFormProps) {
  const { refetch } = usePersistentChat();
  const [userMessage, setUserMessage] = useState(messageSummary);
  const [submitState, setSubmitState] = useState<SubmitState>(submissionStatus ?? "idle");
  const [persistedError, setPersistedError] = useState(submissionStatus === "error");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isResettingPersistedState, setIsResettingPersistedState] = useState(false);
  const hasTrackedImpression = useRef(false);

  const persistSubmissionState = async (
    nextStatus?: "success" | "error" | null,
    interaction?: "shown" | "reset_error",
  ) => {
    try {
      await Auth.fetchWithIdToken("/api/ai/agenticChat/support-request/state", {
        chatId,
        messageId,
        toolCallId,
        ...(nextStatus !== undefined ? { submissionStatus: nextStatus } : {}),
        interaction,
        category,
      });
      refetch();
      return true;
    } catch (_error) {
      return false;
    }
  };

  const handleTryAgain = async () => {
    setErrorMessage(null);
    setIsResettingPersistedState(true);

    try {
      const persisted = await persistSubmissionState(null, "reset_error");
      if (!persisted) {
        return;
      }

      setSubmitState("idle");
      setPersistedError(false);
    } finally {
      setIsResettingPersistedState(false);
    }
  };

  useEffect(() => {
    if (hasTrackedImpression.current || submissionStatus === "success") {
      return;
    }

    void (async () => {
      const persisted = await persistSubmissionState(undefined, "shown");
      if (persisted) {
        hasTrackedImpression.current = true;
      }
    })();
  }, [submissionStatus]);

  const handleSubmit = async () => {
    if (!userMessage.trim()) {
      setErrorMessage("Please add a message for support");
      return;
    }

    setSubmitState("submitting");
    setErrorMessage(null);

    try {
      await Auth.fetchWithIdToken("/api/ai/agenticChat/support-request", {
        chatId,
        messageId,
        toolCallId,
        projectId,
        invocationId: invocationId || null,
        agentMessageSummary: messageSummary,
        userMessage: userMessage.trim(),
        category,
      });
      setSubmitState("success");
      setPersistedError(false);
      refetch();
    } catch (error) {
      setSubmitState("error");
      setErrorMessage(error instanceof Error ? error.message : "Failed to submit support request");
      const persisted = await persistSubmissionState("error");
      setPersistedError(persisted);
    }
  };

  return (
    <ChatSupportRequestFormChrome
      category={category}
      submitState={submitState}
      userMessage={userMessage}
      onUserMessageChange={setUserMessage}
      renderTextarea={() => (
        <Textarea
          mode="default"
          value={userMessage}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setUserMessage(event.target.value)}
          placeholder="Describe what you need help with"
        />
      )}
      onSubmit={handleSubmit}
      onTryAgain={handleTryAgain}
      submitDisabled={!userMessage.trim()}
      submitting={submitState === "submitting"}
      resetting={isResettingPersistedState}
      errorMessage={errorMessage}
      persistedError={persistedError}
      sendIcon={(
        <InlineSVG
          src="/assets/send.svg"
          className="w-4 h-4 [&_path]:fill-white"
        />
      )}
    />
  );
}
